import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Code2, Sun, Moon, LogOut, User, BookOpen, ArrowLeft,
  Users, GraduationCap, Award, Clock, TrendingUp, Search,
  CheckCircle, XCircle, Star, BarChart3, Calendar, Target,
  Trophy, Flame, Activity, Medal
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Header component
const PageHeader = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  return (
    <header className="glass sticky top-0 z-50 border-b border-border px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold">Student Performance</span>
              <p className="text-xs text-muted-foreground">Track & monitor student progress</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/profile')}>
            <User className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={logout}>
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
};

// Overview Stat Card
const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Student Card in list
const StudentCard = ({ student, onSelect, isSelected, rank }) => (
  <div
    onClick={() => onSelect(student)}
    className={`p-3 rounded-xl border cursor-pointer transition-all ${
      isSelected ? 'border-primary bg-primary/8 shadow-sm' : 'border-border hover:border-primary/40 hover:bg-muted/30'
    }`}
    data-testid={`student-card-${student.user_id}`}
  >
    <div className="flex items-center gap-3">
      {rank && rank <= 3 ? (
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
          rank === 1 ? 'bg-yellow-400 text-yellow-900' :
          rank === 2 ? 'bg-slate-300 text-slate-700' :
          'bg-amber-700 text-amber-100'
        }`}>
          {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
        </div>
      ) : (
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/70 to-secondary/70 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {student.name?.charAt(0)?.toUpperCase() || 'S'}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm truncate">{student.name}</h4>
        <p className="text-xs text-muted-foreground truncate">{student.email}</p>
      </div>
      {student.points > 0 && (
        <Badge variant="secondary" className="gap-1 text-xs flex-shrink-0">
          <Star className="w-3 h-3" /> {student.points}
        </Badge>
      )}
    </div>
  </div>
);

// Course Progress Card
const CourseProgressCard = ({ enrollment }) => {
  const progressPercent = enrollment.total_chapters
    ? Math.round((enrollment.chapters_completed / enrollment.total_chapters) * 100)
    : 0;
  const statusColor = {
    approved: 'bg-blue-500/10 text-blue-600 border-blue-200',
    in_progress: 'bg-amber-500/10 text-amber-600 border-amber-200',
    completed: 'bg-green-500/10 text-green-600 border-green-200',
    pending: 'bg-gray-500/10 text-gray-500 border-gray-200',
  }[enrollment.status] || 'bg-gray-500/10 text-gray-500 border-gray-200';

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3 gap-2">
          <h4 className="font-medium text-sm leading-tight">{enrollment.course_title || 'Course'}</h4>
          <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${statusColor}`}>
            {enrollment.status}
          </span>
        </div>
        <div className="space-y-2 mb-3">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Progress</span>
            <span className="font-medium">{enrollment.chapters_completed || 0} / {enrollment.total_chapters || 12} chapters</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressPercent === 100 ? 'bg-green-500' : 'bg-primary'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground">{progressPercent}% complete</span>
        </div>
        {enrollment.exam_score != null && (
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {enrollment.exam_passed ? <CheckCircle className="w-3 h-3 text-green-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
              Exam
            </span>
            <span className={`text-sm font-bold ${enrollment.exam_score >= 60 ? 'text-green-500' : 'text-red-500'}`}>
              {enrollment.exam_score?.toFixed(1)}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Top Performers leaderboard
const TopPerformers = ({ students, onSelect }) => {
  const top3 = [...students].sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 3);
  if (top3.every(s => (s.points || 0) === 0)) return null;
  return (
    <Card className="border-amber-200 dark:border-amber-900/40">
      <CardHeader className="py-3 px-4 border-b border-border">
        <CardTitle className="text-sm flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          Top Performers
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-2">
        {top3.map((s, i) => (
          <div key={s.user_id} onClick={() => onSelect(s)}
            className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
            <span className="text-base">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{s.name}</p>
            </div>
            <Badge variant="secondary" className="text-xs gap-1">
              <Star className="w-3 h-3 text-amber-400" /> {s.points}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// Detail skeleton
const DetailSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-28 bg-muted rounded-xl" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1,2,3,4].map(i => <div key={i} className="h-20 bg-muted rounded-xl" />)}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1,2].map(i => <div key={i} className="h-32 bg-muted rounded-xl" />)}
    </div>
  </div>
);

// Main Component
const StudentPerformance = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const grades = ['5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th', 'College', 'Professional'];

  useEffect(() => { fetchSchools(); fetchStudents(); }, []);
  useEffect(() => {
    if (selectedStudent) fetchStudentDetails(selectedStudent.user_id);
  }, [selectedStudent]);

  const fetchSchools = async () => {
    try {
      const res = await fetch(`${API_URL}/api/organizations`, { headers: getHeaders() });
      if (res.ok) setSchools(await res.json());
    } catch {}
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/students`, { headers: getHeaders() });
      if (res.ok) setStudents(await res.json());
    } catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  };

  const fetchStudentDetails = async (userId) => {
    setLoadingDetails(true);
    setStudentDetails(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/details`, { headers: getHeaders() });
      if (res.ok) setStudentDetails(await res.json());
    } catch {}
    finally { setLoadingDetails(false); }
  };

  const filteredStudents = useMemo(() => {
    let filtered = students;
    if (selectedSchool !== 'all') filtered = filtered.filter(s => s.organization_id === selectedSchool);
    if (selectedGrade !== 'all') filtered = filtered.filter(s => s.grade === selectedGrade);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(s => s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q));
    }
    return filtered;
  }, [students, selectedSchool, selectedGrade, searchQuery]);

  // Overview stats computed from all students
  const overviewStats = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now - 30 * 24 * 3600 * 1000);
    const newThisMonth = students.filter(s => new Date(s.created_at) > thirtyDaysAgo).length;
    const topPoints = students.length > 0 ? Math.max(...students.map(s => s.points || 0)) : 0;
    const topStudent = students.find(s => (s.points || 0) === topPoints);
    return { total: students.length, newThisMonth, topPoints, topStudentName: topStudent?.name || 'None' };
  }, [students]);

  const studentStats = useMemo(() => {
    if (!studentDetails) return null;
    const enrollments = studentDetails.enrollments || [];
    const completedCourses = enrollments.filter(e => e.status === 'completed').length;
    const examsPassed = enrollments.filter(e => e.exam_passed).length;
    const scoredExams = enrollments.filter(e => e.exam_score != null);
    const avgScore = scoredExams.length > 0
      ? scoredExams.reduce((sum, e) => sum + e.exam_score, 0) / scoredExams.length : 0;
    const totalChapters = enrollments.reduce((sum, e) => sum + (e.chapters_completed || 0), 0);
    return {
      totalCourses: enrollments.length, completedCourses, examsPassed, avgScore,
      totalChapters, points: studentDetails.points || 0, badges: studentDetails.badges || [],
    };
  }, [studentDetails]);

  if (!user || !['super_admin', 'org_admin', 'school_admin', 'faculty'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="student-performance-page">
      <PageHeader />
      <main className="container mx-auto px-6 py-6 space-y-6">

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Students" value={overviewStats.total}
            color="from-violet-500 to-purple-600" subtext="Registered on platform" />
          <StatCard icon={Activity} label="New This Month" value={overviewStats.newThisMonth}
            color="from-blue-500 to-indigo-600" subtext="Last 30 days" />
          <StatCard icon={Flame} label="Top Points" value={overviewStats.topPoints}
            color="from-orange-500 to-red-600" subtext={overviewStats.topStudentName} />
          <StatCard icon={Medal} label="Registered" value={students.length > 0 ? `${students.length}` : '—'}
            color="from-emerald-500 to-teal-600" subtext="All roles" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Filters + Student List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 text-sm"
                data-testid="student-search"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select value={selectedSchool} onValueChange={setSelectedSchool}>
                <SelectTrigger data-testid="school-filter" className="text-xs flex-1">
                  <SelectValue placeholder="School" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Schools</SelectItem>
                  {schools.map(school => (
                    <SelectItem key={school.org_id} value={school.org_id}>{school.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                <SelectTrigger data-testid="grade-filter" className="text-xs flex-1">
                  <SelectValue placeholder="Grade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Top Performers */}
            <TopPerformers students={students} onSelect={setSelectedStudent} />

            {/* Student List */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1 text-muted-foreground uppercase tracking-wide">
                <Users className="w-4 h-4" /> Students ({filteredStudents.length})
              </h3>
              {loading ? (
                <div className="space-y-2">
                  {[1,2,3,4,5].map(i => <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />)}
                </div>
              ) : filteredStudents.length === 0 ? (
                <Card className="p-6 text-center">
                  <Users className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No students found</p>
                </Card>
              ) : (
                <ScrollArea className="h-[420px] pr-1">
                  <div className="space-y-2">
                    {filteredStudents.map((student, idx) => (
                      <StudentCard
                        key={student.user_id}
                        student={student}
                        rank={idx + 1}
                        onSelect={setSelectedStudent}
                        isSelected={selectedStudent?.user_id === student.user_id}
                      />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>

          {/* Right: Student Detail */}
          <div className="lg:col-span-3">
            {loadingDetails ? (
              <DetailSkeleton />
            ) : selectedStudent ? (
              <div className="space-y-5">
                {/* Profile Header Card */}
                <Card className="overflow-hidden">
                  <div className="h-16 bg-gradient-to-r from-violet-500/20 via-purple-500/10 to-blue-500/20" />
                  <CardContent className="p-5 -mt-8">
                    <div className="flex items-end gap-4">
                      {selectedStudent.picture ? (
                        <img src={selectedStudent.picture} alt={selectedStudent.name}
                          className="w-16 h-16 rounded-full border-4 border-background object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-full border-4 border-background bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-2xl font-bold">
                          {selectedStudent.name?.charAt(0)?.toUpperCase() || 'S'}
                        </div>
                      )}
                      <div className="flex-1 pb-1">
                        <h2 className="text-xl font-bold">{selectedStudent.name}</h2>
                        <p className="text-sm text-muted-foreground">{selectedStudent.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{selectedStudent.role?.replace('_', ' ') || 'Student'}</Badge>
                          {selectedStudent.grade && <Badge variant="secondary" className="text-xs">{selectedStudent.grade}</Badge>}
                          <span className="text-xs text-muted-foreground">Joined {new Date(selectedStudent.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Grid */}
                {studentStats && (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {[
                      { icon: BookOpen, label: 'Enrolled', value: studentStats.totalCourses, color: 'from-violet-500 to-purple-600' },
                      { icon: CheckCircle, label: 'Completed', value: studentStats.completedCourses, color: 'from-green-500 to-emerald-600' },
                      { icon: Award, label: 'Passed', value: studentStats.examsPassed, color: 'from-blue-500 to-indigo-600' },
                      { icon: Target, label: 'Avg Score', value: `${studentStats.avgScore.toFixed(0)}%`, color: 'from-orange-500 to-red-600' },
                      { icon: GraduationCap, label: 'Chapters', value: studentStats.totalChapters, color: 'from-teal-500 to-cyan-600' },
                      { icon: Star, label: 'Points', value: studentStats.points, color: 'from-yellow-500 to-amber-600' },
                    ].map(({ icon: Icon, label, value, color }) => (
                      <Card key={label}>
                        <CardContent className="p-3 text-center">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mx-auto mb-1`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <p className="text-lg font-bold">{value}</p>
                          <p className="text-xs text-muted-foreground">{label}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Course Enrollments */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 uppercase tracking-wide text-muted-foreground">
                    <BookOpen className="w-4 h-4" /> Course Enrollments
                  </h3>
                  {studentDetails?.enrollments?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {studentDetails.enrollments.map((enrollment, idx) => (
                        <CourseProgressCard key={idx} enrollment={enrollment} />
                      ))}
                    </div>
                  ) : (
                    <Card className="p-8 text-center">
                      <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No course enrollments yet</p>
                    </Card>
                  )}
                </div>

                {/* Badges */}
                {studentStats?.badges?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 uppercase tracking-wide text-muted-foreground">
                      <Award className="w-4 h-4" /> Badges Earned
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {studentStats.badges.map((badge, idx) => (
                        <Badge key={idx} variant="secondary" className="py-1.5 px-3 gap-1">
                          <Award className="w-3 h-3" /> {badge}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Card className="h-full min-h-[400px] flex items-center justify-center">
                <div className="text-center p-12">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Select a Student</h3>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Click on any student from the list to view their detailed performance dashboard
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentPerformance;
